import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { Mail, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Forgot Password screen — self-contained with its own state.
 * Calls onBack() to return to the Sign In view.
 */
const ForgotPasswordView = ({ onBack }) => {
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Please enter your email');
      return;
    }
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
      toast.success('If an account exists, a reset link has been sent to your email.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleClose = () => {
    setForgotSent(false);
    setForgotEmail('');
    onBack();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="py-20 px-4">
        <Card className="max-w-md mx-auto">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl">
              {forgotSent ? 'Check Your Email' : 'Forgot Password?'}
            </CardTitle>
            <p className="text-gray-500 text-sm mt-1">
              {forgotSent ? 'We sent a password reset link to your email' : "Enter your email and we'll send you a reset link"}
            </p>
          </CardHeader>
          <CardContent>
            {forgotSent ? (
              <div className="space-y-4 text-center">
                <p className="text-sm text-gray-600">
                  Check your inbox for <strong>{forgotEmail}</strong>. The link expires in 1 hour.
                </p>
                <p className="text-xs text-gray-400">Didn't receive it? Check your spam folder or try again.</p>
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => setForgotSent(false)} data-testid="try-again-btn">
                    Try Again
                  </Button>
                  <Button className="flex-1 bg-green-600 hover:bg-green-700" onClick={handleClose} data-testid="back-to-login-btn">
                    Back to Sign In
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="pl-10"
                      required
                      data-testid="forgot-email"
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full bg-green-600 hover:bg-green-700" disabled={forgotLoading} data-testid="send-reset-btn">
                  {forgotLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Send Reset Link
                </Button>
                <button
                  type="button"
                  onClick={() => { setForgotEmail(''); onBack(); }}
                  className="w-full text-sm text-gray-500 hover:text-gray-700"
                >
                  Back to Sign In
                </button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPasswordView;
